import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { cn } from '@/utils/cn'
import { sendHelpFeedback, type HelpArticle } from './helpApi'

/**
 * "Was this helpful?" (CLA-1838): each tap records the answer on the server
 * (one per person per article; the latest wins). It shows the answer at once
 * and puts it back if saving fails.
 */
export function HelpFeedback({ article }: { article: HelpArticle }) {
  const queryClient = useQueryClient()
  const [answer, setAnswer] = useState<boolean | null>(article.your_feedback ?? null)
  const [failed, setFailed] = useState(false)

  const record = (helpful: boolean) => {
    const before = answer
    setAnswer(helpful)
    setFailed(false)
    sendHelpFeedback(article.slug, helpful)
      .then(() => {
        queryClient.setQueryData<HelpArticle>(['help', 'article', article.slug], (a) =>
          a ? { ...a, your_feedback: helpful } : a,
        )
      })
      .catch(() => {
        setAnswer(before)
        setFailed(true)
      })
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-ink-faint">
        {failed ? 'Couldn’t save that. Try again.' : answer === null ? 'Was this helpful?' : 'Thanks for telling us.'}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => record(true)}
          aria-label="Helpful"
          aria-pressed={answer === true}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            answer === true ? 'bg-success-light text-on-success-soft' : 'hover:bg-surface-secondary text-ink-faint',
          )}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => record(false)}
          aria-label="Not helpful"
          aria-pressed={answer === false}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            answer === false ? 'bg-error-light text-on-error-soft' : 'hover:bg-surface-secondary text-ink-faint',
          )}
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
